import { useCallback, useEffect, useRef, useState } from 'react'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import { isError } from '@/helpers/checkQuery'
import type { ResponseWrapper } from '@/api/types/base.types'
import { crmIntegrationChatApi } from '../api/crm-integration-chat.api'
import type {
  CrmIntegrationChatListData,
  CrmIntegrationChatListMeta,
  CrmIntegrationConversation,
} from '../types/crm-integration-chat.types'
import { CRM_INTEGRATION_CHAT_DEFAULT_PER_PAGE } from '../types/crm-integration-chat.types'
import { filterIntegrationChatsBySearch } from '../lib/crmIntegrationChatsListSearch'

export type UseCrmIntegrationChatsListParams = {
  enabled?: boolean
  /** Клиентский safety-фильтр; на бэк тоже уходит как provider, если передан */
  provider?: CrmChatProviderKey
  integrationId?: number
  /**
   * Фильтр канала на бэке (`channel_id`):
   * internal id из /integrations/channels или external_channel_id.
   */
  channelId?: string | number | null
  /** user_id менеджера; `0` = без ответственного */
  managerId?: number | null
  search?: string
  /** Мгновенная клиентская фильтрация (до debounce API). */
  searchFilter?: string
  integrationNameById?: Record<number, string>
}

const filterByProvider = (
  items: CrmIntegrationConversation[],
  provider?: CrmChatProviderKey,
): CrmIntegrationConversation[] => {
  if (!provider) {
    return items
  }
  return items.filter((item) => item.provider === provider)
}

const mergeConversationListItems = (
  prev: CrmIntegrationConversation[],
  next: CrmIntegrationConversation[],
): CrmIntegrationConversation[] => {
  if (next.length === 0) {
    return prev
  }

  const seenIds = new Set(prev.map((item) => item.id))
  const merged = [...prev]

  for (const item of next) {
    if (seenIds.has(item.id)) {
      continue
    }
    merged.push(item)
    seenIds.add(item.id)
  }

  return merged
}

const normalizeIntegrationChatListMeta = (
  meta: CrmIntegrationChatListMeta | undefined,
  itemsLength: number,
  page: number,
): CrmIntegrationChatListMeta => {
  const currentPage = meta?.page ?? meta?.currentPage ?? page
  return {
    totalCount: meta?.totalCount ?? itemsLength,
    pageCount: meta?.pageCount ?? (itemsLength > 0 ? 1 : 0),
    currentPage,
    page: currentPage,
    perPage: meta?.perPage ?? CRM_INTEGRATION_CHAT_DEFAULT_PER_PAGE,
  }
}

const parseIntegrationChatListResponse = (
  res: Awaited<ReturnType<typeof crmIntegrationChatApi.getIntegrationChats>>,
  page: number,
  provider?: CrmChatProviderKey,
): { items: CrmIntegrationConversation[]; meta: CrmIntegrationChatListMeta } => {
  if (isError(res)) {
    throw new Error('Не удалось загрузить список чатов')
  }

  const payload = res.data as ResponseWrapper<CrmIntegrationChatListData>
  const data = payload?.data
  const rawItems = Array.isArray(data?.items) ? data.items : []
  const items = filterByProvider(rawItems, provider)

  return {
    items,
    meta: normalizeIntegrationChatListMeta(data?._meta, rawItems.length, page),
  }
}

const loadIntegrationChatsPage = async (
  page: number,
  provider?: CrmChatProviderKey,
  integrationId?: number,
  search?: string,
  channelId?: string | number | null,
  managerId?: number | null,
) => {
  const res = await crmIntegrationChatApi.getIntegrationChats({
    page,
    integrationId,
    channelId: channelId ?? undefined,
    managerId: managerId ?? undefined,
    provider,
    search: search?.trim() || undefined,
  })
  return parseIntegrationChatListResponse(res, page, provider)
}

export const useCrmIntegrationChatsList = ({
  enabled = true,
  provider,
  integrationId,
  channelId,
  managerId = null,
  search,
  searchFilter,
  integrationNameById,
}: UseCrmIntegrationChatsListParams = {}) => {
  const [chats, setChats] = useState<CrmIntegrationConversation[]>([])
  const [meta, setMeta] = useState<CrmIntegrationChatListMeta | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<Error | undefined>()
  const loadMoreInFlightRef = useRef(false)
  const fetchGenerationRef = useRef(0)
  const hasChatsRef = useRef(false)
  const normalizedSearch = search?.trim() ?? ''
  const normalizedSearchFilter = searchFilter?.trim() ?? normalizedSearch
  const normalizedChannelId =
    channelId == null ? null : String(channelId).trim() || null
  const normalizedManagerId =
    managerId != null && Number.isFinite(Number(managerId))
      ? Math.floor(Number(managerId))
      : null

  hasChatsRef.current = chats.length > 0

  const hasMore =
    meta != null &&
    meta.pageCount > 0 &&
    (meta.currentPage ?? meta.page ?? 1) < meta.pageCount

  const fetchFirstPage = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!enabled) {
        setChats([])
        setMeta(null)
        setLoading(false)
        setLoadingMore(false)
        setError(undefined)
        return
      }

      if (!options?.silent && !(normalizedSearch && hasChatsRef.current)) {
        setLoading(true)
        setError(undefined)
      }

      const generation = ++fetchGenerationRef.current

      try {
        const { items, meta: nextMeta } = await loadIntegrationChatsPage(
          1,
          provider,
          integrationId,
          normalizedSearch,
          normalizedChannelId,
          normalizedManagerId,
        )

        if (generation !== fetchGenerationRef.current) {
          return
        }
        setChats(items)
        setMeta(nextMeta)
        setError(undefined)
      } catch (fetchError) {
        if (generation !== fetchGenerationRef.current) {
          return
        }
        if (options?.silent) {
          return
        }

        setError(
          fetchError instanceof Error
            ? fetchError
            : new Error('Не удалось загрузить список чатов'),
        )
      } finally {
        if (generation !== fetchGenerationRef.current) {
          return
        }
        setLoading(false)
      }
    },
    [
      enabled,
      integrationId,
      normalizedChannelId,
      normalizedManagerId,
      normalizedSearch,
      provider,
    ],
  )

  const loadMore = useCallback(async () => {
    if (!enabled || loadMoreInFlightRef.current) {
      return
    }

    const currentMeta = meta
    const currentPage = currentMeta?.currentPage ?? currentMeta?.page ?? 1
    if (
      currentMeta == null ||
      currentMeta.pageCount <= 0 ||
      currentPage >= currentMeta.pageCount
    ) {
      return
    }

    loadMoreInFlightRef.current = true
    setLoadingMore(true)

    try {
      const nextPage = currentPage + 1
      const { items, meta: nextMeta } = await loadIntegrationChatsPage(
        nextPage,
        provider,
        integrationId,
        normalizedSearch,
        normalizedChannelId,
        normalizedManagerId,
      )
      setChats((prev) => mergeConversationListItems(prev, items))
      setMeta(nextMeta)
      setError(undefined)
    } catch {
      // тихо — уже показанные чаты остаются
    } finally {
      loadMoreInFlightRef.current = false
      setLoadingMore(false)
    }
  }, [
    enabled,
    integrationId,
    meta,
    normalizedChannelId,
    normalizedManagerId,
    normalizedSearch,
    provider,
  ])

  const visibleChats = filterIntegrationChatsBySearch(
    chats,
    normalizedSearchFilter,
    integrationNameById,
  )

  useEffect(() => {
    void fetchFirstPage()
  }, [fetchFirstPage])

  const retry = useCallback(() => {
    void fetchFirstPage()
  }, [fetchFirstPage])

  const silentRetry = useCallback(() => {
    return fetchFirstPage({ silent: true })
  }, [fetchFirstPage])

  const clearConversationUnread = useCallback((conversationId: number) => {
    if (!Number.isFinite(conversationId) || conversationId <= 0) {
      return
    }

    setChats((prev) => {
      let changed = false
      const next = prev.map((item) => {
        if (item.id !== conversationId || (item.unread_count ?? 0) <= 0) {
          return item
        }
        changed = true
        return { ...item, unread_count: 0 }
      })
      return changed ? next : prev
    })
  }, [])

  return {
    chats: visibleChats,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    retry,
    silentRetry,
    clearConversationUnread,
  }
}
